import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { refreshStatusBar } from "@/store/ui/actions";
import { type ISignature, SignatureStatus } from "@/services/SignaturesService";
import UserIdentitiesService, { type KycSignatureDto } from "@/services/UserIdentitiesService";
import { obfuscatePhone } from "@/utils/ObfuscatePhone";
import type { KycSignatureControllerReturn, KycSignatureNavigationState, KycSignaturePhase } from "@/screen/Identities/KycSignature/Interfaces/KycSignatureInterfaces";
import { buildIdentitiesListNavigationState, IdentitiesPath, IdentityEditPath } from "@/screen/Identities/identitiesRoutes";

/**
 * Controlador de la ruta de re-firma standalone del KYC, solo para intervinientes OUTDATED.
 *
 * Flujo: se carga el documento KYC real (PDF) del interviniente y se prepara su firma DESARMADA (registro
 * creado, sin SMS). Tras leer el documento, "Firmar KYC" arma esa firma: es entonces cuando se envía el
 * OTP y arranca la ventana de 5 minutos. Al verificar el OTP (`onSigned`), se finaliza en el backend
 * (`signUserIdentityKyc`): generar (idempotente) → firmar en Omnia → activar (OUTDATED→ACTIVE).
 *
 * Todo va cazado por `userIdentityId`: el frontend no maneja en ningún momento el DNI del interviniente.
 */
/**
 * Códigos con prefijo `KYC_` que NO son motivos de "datos sin cumplimentar": aunque comparten prefijo
 * con los de validación, se excluyen del banner de campos pendientes. `KYC_STALE_VERSION_NOT_REFRESHED`
 * cae al aviso genérico; `KYC_ALREADY_SIGNED` se intercepta antes (salida al listado) y queda aquí
 * como red de seguridad.
 */
const KycNonValidationCodes = ["KYC_STALE_VERSION_NOT_REFRESHED", "KYC_ALREADY_SIGNED"];

export function useKycSignatureController(): KycSignatureControllerReturn
{
    const history = useHistory();
    const location = useLocation<KycSignatureNavigationState>();
    const dispatch = useDispatch();

    const userIdentityId = location.state?.userIdentityId;
    const userIdentityName = location.state?.userIdentityName;
    const contractSavingsPlanId = location.state?.contractSavingsPlanId;

    const [phase, setPhase] = useState<KycSignaturePhase>("contract");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    // Motivos de validación KYC que impiden firmar (uno por dato pendiente de cumplimentar). Se listan
    // todos juntos, no solo el primero, para que el usuario sepa exactamente qué le falta rellenar.
    const [kycBlockingReasons, setKycBlockingReasons] = useState<string[] | null>(null);
    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [signatures, setSignatures] = useState<ISignature[]>([]);
    // Firma creada pero aún DESARMADA (sin SMS): la arma el botón "Firmar KYC".
    const [pendingSignatureId, setPendingSignatureId] = useState<number | null>(null);
    // Interviniente cuya carga ya se ha lanzado. La generación del KYC no es una consulta inocua (valida,
    // sincroniza y avisa a soporte si falla), así que se pide UNA vez por interviniente aunque el efecto
    // se re-ejecute por un re-render del árbol.
    const requestedIdentityRef = useRef<number | null>(null);
    // Object URL del PDF vivo, para revocarlo al desmontar (si no, el blob se queda en memoria).
    const documentObjectUrlRef = useRef<string | null>(null);

    // Sin id no se puede firmar nada: se vuelve al listado (protección frente a acceso directo).
    useEffect(() =>
    {
        if(!userIdentityId)
        {
            history.replace(IdentitiesPath, buildIdentitiesListNavigationState(contractSavingsPlanId));
        }
    }, [userIdentityId, history, contractSavingsPlanId]);

    // Libera el blob del PDF actual (si lo hay): los object URL viven hasta que se revocan explícitamente.
    const revokeDocumentObjectUrl = useCallback((): void =>
    {
        if(documentObjectUrlRef.current)
        {
            URL.revokeObjectURL(documentObjectUrlRef.current);
            documentObjectUrlRef.current = null;
        }
    }, []);

    // Al abandonar la pantalla se suelta el PDF.
    useEffect(() => revokeDocumentObjectUrl, [revokeDocumentObjectUrl]);

    // Carga del documento KYC real (PDF base64) y preparación de la firma desarmada.
    useEffect(() =>
    {
        if(!userIdentityId || requestedIdentityRef.current === userIdentityId)
        {
            return;
        }
        requestedIdentityRef.current = userIdentityId;

        let cancelled = false;
        setIsLoading(true);
        setLoadError(null);
        setKycBlockingReasons(null);

        // La versión firmable solo existe en Omnia una vez generada, así que se genera (o se reutiliza,
        // porque es idempotente: mismo kycId si ya había pendiente) ANTES de pedir el documento. La
        // generación es además la puerta de validación: si el KYC tiene datos incompletos/bloqueantes, aquí
        // sale el error real (en vez del 404 "documento no disponible" que devolvería el visor al no
        // encontrar versión pendiente que servir). Secuencial a propósito: si la validación bloquea, no se
        // llega a crear ninguna firma.
        const load = async (): Promise<void> =>
        {
            const generateEnv = await UserIdentitiesService.generateUserIdentityKyc(userIdentityId);
            if(cancelled)
            {
                return;
            }

            if(!generateEnv.success)
            {
                // El KYC ya está firmado: no hay nada que firmar. Se vuelve al listado como tras un firmado
                // correcto (refresco de barra de estado + navegación), sin banner de "datos sin cumplimentar".
                const alreadySigned = (generateEnv.errors ?? []).some((error) => error.code === "KYC_ALREADY_SIGNED");
                if(alreadySigned)
                {
                    dispatch(refreshStatusBar());
                    history.push(IdentitiesPath, buildIdentitiesListNavigationState(contractSavingsPlanId, "El KYC ya está firmado."));
                    return;
                }

                // La generación valida IronIA-side los datos KYC. Sus motivos (KYC_*) llegan como lista de
                // errores: se muestran TODOS (título + enumeración) para que el usuario sepa qué rellenar.
                // Un fallo sin códigos de validación (infraestructura/Omnia) cae al aviso genérico.
                const validationReasons = (generateEnv.errors ?? [])
                    .filter((error) => !!error.code && error.code.startsWith("KYC_") && !KycNonValidationCodes.includes(error.code))
                    .map((error) => error.message)
                    .filter((message): message is string => !!message);

                if(validationReasons.length > 0)
                {
                    setKycBlockingReasons(validationReasons);
                }
                else
                {
                    setLoadError(generateEnv.errors?.[0]?.message ?? "No se pudo preparar el documento KYC. Inténtalo de nuevo más tarde.");
                }
                return;
            }

            const documentEnv = await UserIdentitiesService.getUserIdentityKycDocument(userIdentityId);
            if(cancelled)
            {
                return;
            }

            if(!documentEnv.success || !documentEnv.data)
            {
                setLoadError(documentEnv.errors?.[0]?.message ?? "No se pudo cargar el documento KYC. Inténtalo de nuevo más tarde.");
                return;
            }

            // Firma creada aquí pero SIN enviar el SMS: quien solo entra a leer su KYC no recibe nada.
            const signatureEnv = await UserIdentitiesService.requestUserIdentityKycSignature(userIdentityId);
            if(cancelled)
            {
                return;
            }

            const preparedSignature = signatureEnv.data as KycSignatureDto;
            if(!signatureEnv.success || typeof preparedSignature?.id !== "number")
            {
                setLoadError(signatureEnv.errors?.[0]?.message ?? "No se pudo preparar la firma del KYC. Inténtalo de nuevo más tarde.");
                return;
            }

            const doc = documentEnv.data;
            setPendingSignatureId(preparedSignature.id);

            // El PDF llega en base64 dentro del envelope, pero NO se puede incrustar como data URI:
            // Chrome bloquea la navegación de nivel superior a `data:` dentro de un iframe, así que el
            // visor no se monta y se acaba viendo el base64 crudo (además de meter megas de string en el
            // DOM). Se materializa como blob y se incrusta por object URL, igual que el resto de adjuntos.
            const objectUrl = base64ToObjectUrl(doc.content, doc.mimeType || "application/pdf");
            if(!objectUrl)
            {
                setLoadError("No se pudo cargar el documento KYC. Inténtalo de nuevo más tarde.");
                return;
            }

            revokeDocumentObjectUrl();
            documentObjectUrlRef.current = objectUrl;
            setDocumentUrl(objectUrl);
        };

        load()
        .catch(() =>
        {
            if(!cancelled)
            {
                setLoadError("No se pudo cargar el documento KYC. Inténtalo de nuevo más tarde.");
            }
        })
        .finally(() =>
        {
            if(!cancelled)
            {
                setIsLoading(false);
            }
        });

        return () =>
        {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdentityId]);

    // Mientras no haya firmas armadas, la fase de firma aún no está lista.
    const isSignatureIntegrationPending = signatures.length === 0;

    // Teléfono de firma ofuscado, para indicar al interviniente a dónde se ha enviado el SMS sin mostrar el
    // número completo. Llega en la firma armada (`phone`), así que solo está disponible en la fase de firma.
    const obfuscatedSignPhone = obfuscatePhone(signatures[0]?.phone);

    // "Firmar KYC": arma la firma ya creada (envía el OTP y abre la ventana) y pasa a la fase de firma.
    const handleProceedToSignature = useCallback((): void =>
    {
        if(!userIdentityId || pendingSignatureId == null)
        {
            setLoadError("No se pudo iniciar la firma del KYC. Inténtalo de nuevo más tarde.");
            return;
        }

        setIsLoading(true);
        UserIdentitiesService.sendUserIdentityKycSignature(userIdentityId, pendingSignatureId)
        .then((env) =>
        {
            const armedSignature = env.data as KycSignatureDto;
            if(!env.success || typeof armedSignature?.id !== "number")
            {
                setLoadError(env.errors?.[0]?.message ?? "No se pudo generar la firma del KYC. Inténtalo de nuevo más tarde.");
                return;
            }
            setSignatures([toSignature(armedSignature)]);
            setPhase("signature");
        })
        .catch(() =>
        {
            setLoadError("No se pudo generar la firma del KYC. Inténtalo de nuevo más tarde.");
        })
        .finally(() =>
        {
            setIsLoading(false);
        });
    }, [userIdentityId, pendingSignatureId]);

    const handleBackToContract = useCallback((): void =>
    {
        setPhase("contract");
    }, []);

    const handleCancel = useCallback((): void =>
    {
        history.push(IdentitiesPath, buildIdentitiesListNavigationState(contractSavingsPlanId));
    }, [history, contractSavingsPlanId]);

    const handleGoToEdit = useCallback((): void =>
    {
        if(!userIdentityId)
        {
            return;
        }
        history.push(IdentityEditPath, {userIdentityId, contractSavingsPlanId});
    }, [history, userIdentityId, contractSavingsPlanId]);

    // OTP ya verificado por el SignatureComponent (PUT signatures): finaliza en el backend.
    const handleSigned = useCallback((): void =>
    {
        const signatureId = signatures[0]?.id;
        if(!userIdentityId || signatureId == null)
        {
            setLoadError("No se pudo completar la firma del KYC. Inténtalo de nuevo más tarde.");
            return;
        }

        setIsLoading(true);
        UserIdentitiesService.signUserIdentityKyc(userIdentityId, signatureId)
        .then((env) =>
        {
            if(!env.success)
            {
                setLoadError(env.errors?.[0]?.message ?? "No se pudo completar la firma del KYC.");
                return;
            }
            // El backend ya ha retirado el aviso de este interviniente al firmar; esto hace que la barra de
            // estado lo vuelva a pedir y desaparezca ahora, sin esperar a una recarga de página.
            dispatch(refreshStatusBar());
            history.push(IdentitiesPath, buildIdentitiesListNavigationState(contractSavingsPlanId, "El KYC se ha firmado correctamente."));
        })
        .catch(() =>
        {
            setLoadError("No se pudo completar la firma del KYC. Inténtalo de nuevo más tarde.");
        })
        .finally(() =>
        {
            setIsLoading(false);
        });
    }, [userIdentityId, signatures, history, dispatch, contractSavingsPlanId]);

    const handleSignaturesChanged = useCallback((updated: ISignature[]): void =>
    {
        setSignatures(updated);
    }, []);

    return{
        userIdentityId,
        userIdentityName,
        phase,
        isLoading,
        loadError,
        kycBlockingReasons,
        documentUrl,
        signatures,
        obfuscatedSignPhone,
        isSignatureIntegrationPending,
        handleProceedToSignature,
        handleBackToContract,
        handleCancel,
        handleGoToEdit,
        handleSigned,
        handleSignaturesChanged,
    };
}

/**
 * Convierte el contenido base64 del documento en un object URL (blob) incrustable en el iframe.
 * Devuelve null si el contenido viene vacío o no es base64 válido.
 */
function base64ToObjectUrl(content: string | undefined | null, mimeType: string): string | null
{
    if(!content)
    {
        return null;
    }

    try
    {
        // Se admite tanto el base64 pelado como una data URI completa, por si el backend cambiara de forma.
        const base64 = content.includes(",") && content.startsWith("data:") ? content.slice(content.indexOf(",") + 1) : content;
        const binary = window.atob(base64);
        const bytes = new Uint8Array(binary.length);
        for(let index = 0; index < binary.length; index++)
        {
            bytes[index] = binary.charCodeAt(index);
        }
        return URL.createObjectURL(new Blob([bytes], {type: mimeType}));
    }
    catch
    {
        return null;
    }
}

/** Adapta la firma del KYC al contrato que consume el SignatureComponent compartido. */
function toSignature(dto: KycSignatureDto): ISignature
{
    return{
        id: dto.id,
        createdAt: dto.createdAt,
        expiresAt: dto.expiresAt ?? undefined,
        signAt: dto.signAt ?? undefined,
        phone: dto.phone ?? undefined,
        status: dto.status as SignatureStatus,
    };
}
